/**
 * Control Tower — top-level sidebar group (owner-only, role 1).
 *
 * Shailendra's exception dashboards: one place to see what's OFF across the
 * business so nothing festers unnoticed for a week. Each sub-page is a full
 * table with red-flag highlighting on the problem rows.
 *
 * Sections (spec: florida-ai-local/dashboard-ai-agent/specs/owner-command-center.md):
 *   1. Dispatch          — undispatched aging orders (>3d / >5d, prepaid >2d)  [BUILT]
 *   2. Repeat Agents     — who isn't working / behind target                   [planned]
 *   3. Managers          — which manager isn't managing                        [planned]
 *   4. Lead Distribution — routing efficiency + gaps                           [planned]
 *   5. New Agents        — leads / revenue / tenure / salary ratios            [planned]
 *
 * Add a section by dropping an entry in SECTIONS + its page + route.
 */

import React from "react";
import { FormattedMessage } from "react-intl";
import { IconRadar2, IconTruckDelivery, IconUserPlus, IconCalendarStats, IconRepeat, IconPhoneCall, IconFileSpreadsheet, IconBrandWhatsapp } from "@tabler/icons";
import { getDecryptDataFromLocal } from "../../utils/encrypt";

const role = Number(getDecryptDataFromLocal("setRole"));
// Owner = Shailendra (1) + Ameen (17); they see ALL Control Tower items.
// Managers (13/14) see the group too, but ONLY the manager-safe items (Daily
// Review) — the owner-level exception dashboards stay hidden. Shailendra 2026-07-24.
const IS_OWNER   = role === 1 || role === 17;
const IS_MANAGER = role === 13 || role === 14;
// Bulk team (Manjay/Ravi) — bulkWaManager and whatsappTeam are already
// stashed in localStorage at login (JWTLogin.js dataSet), no extra fetch
// needed. Gets the same 6 report tabs as owner, server-scoped to bulk-only
// data (see resolveReportViewerScope in manijment-api); explicitly NOT
// day-wise-revenue (not requested) and NOT whatsapp-blast (stays role1Only,
// also hard-enforced server-side regardless of this flag). Shailendra 2026-09-03.
const IS_BULK_TEAM = localStorage.getItem("bulkWaManager") === "true"
  || (role === 14 && localStorage.getItem("whatsappTeam") === "bulk");
export const SHOULD_SHOW_CONTROL_TOWER = IS_OWNER || IS_MANAGER || IS_BULK_TEAM;

const icons = { IconRadar2, IconTruckDelivery, IconUserPlus, IconCalendarStats, IconRepeat, IconPhoneCall, IconFileSpreadsheet, IconBrandWhatsapp };

// `managerSafe: true` items are visible to managers; everything else is owner-only.
// `role1Only: true` narrows further — visible to Shailendra (role 1) even though
// Ameen (role 17) is otherwise treated as owner-equivalent everywhere else here.
// `bulkTeamSafe: true` — visible to Manjay/Ravi's bulk team (IS_BULK_TEAM); the
// data itself is server-scoped to bulk-only regardless of this flag, this just
// controls which tabs even appear. Shailendra 2026-09-03.
// `adminOrAmeenOnly: true` — role 1 and Ameen by id; other role 17s (Lalit) don't get it.
const SECTIONS = [
  { key: "dispatch",     url: "/dashboard/control-tower/dispatch",     icon: icons.IconTruckDelivery, label: "Dispatch", bulkTeamSafe: true },
  { key: "repeat",       url: "/dashboard/control-tower/repeat",       icon: icons.IconRepeat,        label: "Repeat", bulkTeamSafe: true },
  { key: "calling-efficiency", url: "/dashboard/control-tower/calling-efficiency", icon: icons.IconPhoneCall, label: "Calling Efficiency", bulkTeamSafe: true },
  { key: "daily-review",       url: "/dashboard/control-tower/daily-review",       icon: icons.IconCalendarStats, label: "Daily Review", managerSafe: true, bulkTeamSafe: true },
  { key: "new-agents",   url: "/dashboard/control-tower/new-agents",   icon: icons.IconUserPlus,      label: "New Agents", bulkTeamSafe: true },
  { key: "day-wise-revenue", url: "/dashboard/control-tower/day-wise-revenue", icon: icons.IconCalendarStats, label: "Day-Wise Revenue" },
  { key: "cohort-assignment", url: "/dashboard/control-tower/cohort-assignment", icon: icons.IconFileSpreadsheet, label: "Cohort Assignment Report", managerSafe: true, bulkTeamSafe: true },
  { key: "cohort-responses", url: "/dashboard/control-tower/cohort-responses", icon: icons.IconFileSpreadsheet, label: "Cohort Call Responses", managerSafe: true },
  { key: "cohort-buyers", url: "/dashboard/control-tower/cohort-buyers", icon: icons.IconFileSpreadsheet, label: "Cohort Buyers M0-M10", adminOrAmeenOnly: true },
  { key: "whatsapp-blast", url: "/dashboard/control-tower/whatsapp-blast", icon: icons.IconBrandWhatsapp, label: "WhatsApp Blast", role1Only: true },
];

// Owner → all sections (minus role1Only ones unless you ARE role 1); bulk team
// → only bulkTeamSafe ones (checked BEFORE plain manager, since Ravi is also
// userType 14/IS_MANAGER — he must get the wider bulk set, not just the two
// managerSafe tabs); manager → only managerSafe ones.
const IS_AMEEN = localStorage.getItem("adminId") === "63ddfd41a136bb63d35bf21b";
const visibleSections = IS_OWNER
  ? SECTIONS.filter(s => (!s.role1Only || role === 1) && (!s.adminOrAmeenOnly || role === 1 || IS_AMEEN))
  : (IS_BULK_TEAM ? SECTIONS.filter(s => s.bulkTeamSafe) : (IS_MANAGER ? SECTIONS.filter(s => s.managerSafe) : []));
const children = visibleSections.map(s => ({
  id:          `ct-${s.key}`,
  title:       <FormattedMessage id={s.label} defaultMessage={s.label} />,
  type:        "item",
  url:         s.url,
  icon:        s.icon,
  breadcrumbs: false,
}));

export const ControlTowerTab = {
  id: "control-tower",
  title: <FormattedMessage id="Control Tower" defaultMessage="Control Tower" />,
  type: "group",
  children: children.length > 0 ? [
    {
      id: "ct-group-root",
      title: <FormattedMessage id="Control Tower" defaultMessage="Control Tower" />,
      type: "collapse",
      icon: icons.IconRadar2,
      children,
    },
  ] : [],
};
